import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Check, Sun, Moon, Shuffle, Database, IdentificationCard, LockKey } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  brazilianStates,
  generateFullName,
  generateCPF,
  generatePhone,
  getCitiesForState,
  getRandomState,
  fetchAddressByCEP,
  formatCep,
} from "@/utils/brazilianData";
import { pickRandomLocalCep, citiesAvailableForState } from "@/utils/cepDatabase";

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [selectedUF, setSelectedUF] = useState("random");
  const [selectedCity, setSelectedCity] = useState("random");
  const [availableCities, setAvailableCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [noFormatting, setNoFormatting] = useState(true);
  const [noAddressPunctuation, setNoAddressPunctuation] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  useEffect(() => {
    if (selectedUF && selectedUF !== 'random') {
      // Cruza cidades pré-definidas com as que realmente têm CEPs na base
      const predefinedCities = getCitiesForState(selectedUF);
      const availableCitySet = citiesAvailableForState(selectedUF);
      const cities = predefinedCities.filter((c) => availableCitySet.has(c.name));
      setAvailableCities(cities);
      setSelectedCity("random");
    } else {
      setAvailableCities([]);
      setSelectedCity("random");
    }
  }, [selectedUF]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const handleGenerateData = async () => {
    if (!selectedUF) return;

    setIsLoading(true);
    setGeneratedData(null);

    try {
      const actualUF = selectedUF === 'random' ? getRandomState() : selectedUF;
      const nome = generateFullName();
      const cpf = generateCPF();
      const telefone = generatePhone(actualUF);

      // 1) Tenta base local primeiro (nativo, instantâneo)
      // Passa cidade só quando usuário escolheu UF específica e cidade específica
      const cityFilter = (selectedUF !== 'random' && selectedCity !== 'random')
        ? selectedCity
        : null;
      const localCep = pickRandomLocalCep(actualUF, cityFilter);
      let cepData = null;

      if (localCep) {
        cepData = {
          cep: formatCep(localCep.cep),
          logradouro: localCep.logradouro,
          bairro: localCep.bairro,
          cidade: localCep.cidade,
          estado: localCep.estado,
        };
      } else {
        // 2) Fallback raríssimo: estado sem base local — tenta ViaCEP usando um CEP aleatório do estado
        // (só ocorre se algum import falhar; mantido por segurança)
        setIsLoading(false);
        return;
      }

      const numeroEndereco = Math.floor(Math.random() * (900 - 10 + 1)) + 10;
      const logradouroComNumero = `${cepData.logradouro}, ${numeroEndereco}`;
      const enderecoCompleto = `${logradouroComNumero}, ${cepData.bairro}, ${cepData.cidade} - ${cepData.estado}`;

      setGeneratedData({
        nome,
        cpf,
        cep: cepData.cep,
        telefone,
        endereco: enderecoCompleto,
        endereco2: logradouroComNumero,
      });
    } catch (error) {
      // Silent
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        // Silent
      }
      
      document.body.removeChild(textArea);
    }
  };

  const removeFormatting = {
    cpf: (value) => value.replace(/\D/g, ''),
    cep: (value) => value.replace(/\D/g, ''),
    telefone: (value) => value.replace(/\D/g, ''),
  };

  const stripAddressPunctuation = (value) =>
    value.replace(/[,\-]/g, ' ').replace(/\s+/g, ' ').trim();

  const getDisplayValue = (key, value) => {
    if (noFormatting && ['cpf', 'cep', 'telefone'].includes(key)) {
      return removeFormatting[key](value);
    }
    if (noAddressPunctuation && ['endereco', 'endereco2'].includes(key)) {
      return stripAddressPunctuation(value);
    }
    return value;
  };

  const copyAllData = async () => {
    if (!generatedData) return;

    const cpfValue = getDisplayValue('cpf', generatedData.cpf);
    const cepValue = getDisplayValue('cep', generatedData.cep);
    const telefoneValue = getDisplayValue('telefone', generatedData.telefone);
    const enderecoValue = getDisplayValue('endereco', generatedData.endereco);
    const endereco2Value = getDisplayValue('endereco2', generatedData.endereco2);

    const formattedData = `Nome: ${generatedData.nome}
CPF: ${cpfValue}
CEP: ${cepValue}
Telefone: ${telefoneValue}
Endereço: ${enderecoValue}
Endereço 2: ${endereco2Value}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(formattedData);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = formattedData;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopiedField("all");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = formattedData;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopiedField("all");
        setTimeout(() => setCopiedField(null), 2000);
      } catch (err) {
        // Silent
      }
      
      document.body.removeChild(textArea);
    }
  };

  const dataFields = generatedData
    ? [
        { label: "Nome Completo", value: generatedData.nome, key: "nome" },
        { label: "CPF", value: getDisplayValue('cpf', generatedData.cpf), key: "cpf" },
        { label: "CEP", value: getDisplayValue('cep', generatedData.cep), key: "cep" },
        { label: "Telefone", value: getDisplayValue('telefone', generatedData.telefone), key: "telefone" },
        { label: "Endereço", value: getDisplayValue('endereco', generatedData.endereco), key: "endereco" },
        { label: "Endereço 2", value: getDisplayValue('endereco2', generatedData.endereco2), key: "endereco2" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] flex items-center justify-center shadow-lg shadow-primary/20">
              <Database size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">
                ClickDados
              </h1>
              <p className="text-[11px] text-muted-foreground -mt-0.5 hidden sm:block">
                Gerador de dados de teste
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/nome-cpf"
              data-testid="nav-nome-cpf-btn"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-secondary hover:bg-accent transition-all duration-200 text-sm font-medium hover:scale-[1.02]"
            >
              <IdentificationCard size={16} weight="bold" className="text-primary" />
              <span>Gerar Nome + CPF</span>
            </Link>
            <Link
              to="/nome-cpf"
              data-testid="nav-nome-cpf-btn-mobile"
              aria-label="Gerar Nome + CPF"
              className="sm:hidden w-9 h-9 rounded-xl bg-secondary hover:bg-accent flex items-center justify-center transition-all duration-200"
            >
              <IdentificationCard size={18} weight="bold" className="text-primary" />
            </Link>
            <Link
              to="/senha"
              data-testid="nav-senha-btn"
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-secondary hover:bg-accent transition-all duration-200 text-sm font-medium hover:scale-[1.02]"
            >
              <LockKey size={16} weight="bold" className="text-primary" />
              <span>Gerar Senha</span>
            </Link>
            <Link
              to="/senha"
              data-testid="nav-senha-btn-mobile"
              aria-label="Gerar Senha"
              className="sm:hidden w-9 h-9 rounded-xl bg-secondary hover:bg-accent flex items-center justify-center transition-all duration-200"
            >
              <LockKey size={18} weight="bold" className="text-primary" />
            </Link>
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              className="w-9 h-9 rounded-xl bg-secondary hover:bg-accent flex items-center justify-center transition-all duration-200 hover:scale-105"
              aria-label="Toggle theme"
            >
              {darkMode ? (
                <Sun size={18} weight="bold" className="text-amber-500" />
              ) : (
                <Moon size={18} weight="bold" className="text-slate-600" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10 sm:py-14">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
            Gere dados brasileiros válidos
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            CPF, telefone, CEP e endereços reais para testes de formulários
          </p>
        </div>

        {/* Controls Card */}
        <div className="bg-card rounded-2xl border border-border/50 shadow-sm p-6 sm:p-8 mb-8">
          <div className="flex flex-col gap-6">
            {/* Selectors Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Estado (UF)
                </label>
                <Select value={selectedUF} onValueChange={setSelectedUF}>
                  <SelectTrigger 
                    data-testid="uf-select"
                    className="h-12 rounded-xl border-border/50 bg-secondary/50 hover:bg-secondary transition-colors input-modern"
                  >
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border/50">
                    {brazilianStates.map((state) => (
                      <SelectItem 
                        key={state.uf} 
                        value={state.uf}
                        className="rounded-lg"
                      >
                        {state.emoji ? `${state.emoji} ${state.name}` : `${state.uf} — ${state.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Cidade (opcional)
                </label>
                <Select
                  value={selectedCity}
                  onValueChange={setSelectedCity}
                  disabled={!selectedUF || selectedUF === 'random'}
                >
                  <SelectTrigger 
                    data-testid="city-select"
                    className="h-12 rounded-xl border-border/50 bg-secondary/50 hover:bg-secondary transition-colors disabled:opacity-40 input-modern"
                  >
                    <SelectValue placeholder="Aleatória" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border/50">
                    <SelectItem value="random" className="rounded-lg">
                      Aleatória
                    </SelectItem>
                    {availableCities.map((city) => (
                      <SelectItem 
                        key={city.name} 
                        value={city.name}
                        className="rounded-lg"
                      >
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Checkbox and Button Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="no-formatting"
                    data-testid="no-formatting-checkbox"
                    checked={noFormatting}
                    onCheckedChange={setNoFormatting}
                    className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
                  />
                  <label
                    htmlFor="no-formatting"
                    className="text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    Sem formatação (apenas números)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="no-address-punct"
                    data-testid="no-address-punct-checkbox"
                    checked={noAddressPunctuation}
                    onCheckedChange={setNoAddressPunctuation}
                    className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
                  />
                  <label
                    htmlFor="no-address-punct"
                    className="text-sm text-muted-foreground cursor-pointer select-none"
                  >
                    Endereços sem vírgulas e hífens
                  </label>
                </div>
              </div>
              
              <button
                data-testid="generate-btn"
                onClick={handleGenerateData}
                disabled={isLoading || !selectedUF}
                className="btn-primary h-12 px-8 rounded-xl text-white font-medium flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
              >
                {isLoading ? (
                  <span className="loading-pulse">Gerando...</span>
                ) : (
                  <>
                    <Shuffle size={18} weight="bold" />
                    Gerar Dados
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Data Grid */}
        <AnimatePresence mode="wait">
          {generatedData && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <div className="data-grid" data-testid="data-grid">
                {dataFields.map((field, index) => (
                  <motion.div
                    key={field.key}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.04 }}
                    className="data-cell p-5 sm:p-6 relative group cursor-pointer"
                    onClick={() => copyToClipboard(field.value, field.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        copyToClipboard(field.value, field.key);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-2 block pointer-events-none">
                          {field.label}
                        </label>
                        <div 
                          className="font-mono text-sm sm:text-[15px] font-medium tracking-tight break-all leading-relaxed group-hover:text-primary transition-colors"
                          data-testid={`data-${field.key}`}
                        >
                          {field.value}
                        </div>
                      </div>
                      <div
                        data-testid={`copy-${field.key}-btn`}
                        className="copy-btn p-2.5 text-muted-foreground group-hover:text-foreground flex-shrink-0"
                        aria-label={`Copiar ${field.label.toLowerCase()}`}
                      >
                        {copiedField === field.key ? (
                          <Check size={16} weight="bold" className="text-emerald-500" />
                        ) : (
                          <Copy size={16} weight="bold" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Copy All Button */}
              <button
                data-testid="copy-all-btn"
                onClick={copyAllData}
                className="mt-4 w-full h-12 rounded-xl border border-border/50 bg-card hover:bg-accent transition-all duration-200 flex items-center justify-center gap-2.5 font-medium card-hover"
              >
                {copiedField === "all" ? (
                  <>
                    <Check size={18} weight="bold" className="text-emerald-500" />
                    <span className="text-emerald-600 dark:text-emerald-400">Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy size={18} weight="bold" />
                    Copiar Tudo
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!generatedData && !isLoading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl border border-dashed border-border/70 bg-card/50 p-12 sm:p-16 text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-secondary mx-auto mb-5 flex items-center justify-center">
              <Database size={24} weight="duotone" className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xs mx-auto">
              Selecione um estado e clique em <span className="font-medium text-foreground">"Gerar Dados"</span> para começar
            </p>
          </motion.div>
        )}

        {/* Footer */}
        <footer className="mt-14 pt-8 border-t border-border/50">
          <div className="text-center space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto">
              Dados gerados aleatoriamente para fins de teste. CPF matematicamente válido, porém fictício. Endereços de base local com referências reais.
            </p>
            <p className="text-xs text-muted-foreground/50">
              Desenvolvido por Erickson
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;
